export interface SerializableSet<T> {
  __type: 'Set';
  value: Array<T>;
}

export interface SerializableMap<T> {
  __type: 'Map';
  value: { [key: string]: T };
}

export interface SerializableDate {
  __type: 'Date';
  value: string;
}

export type SerializableValue<T> =
  | T
  | SerializableSet<T>
  | SerializableMap<T>
  | SerializableDate;

export function transformSetToSerializable<T>(
  value: Set<T>,
): SerializableSet<T> {
  return { __type: 'Set', value: Array.from(value) };
}

export function transformMapToSerializable<K, V>(
  value: Map<K, V>,
): SerializableMap<V> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  return { __type: 'Map', value: Object.fromEntries(value) };
}

export function transformDateToSerializable(value: Date): SerializableDate {
  return { __type: 'Date', value: value.toISOString() };
}

export function transformValueToSerializable<T>(
  value: T,
): SerializableValue<T> {
  if (value instanceof Date) {
    return transformDateToSerializable(value);
  }

  if (value instanceof Set) {
    return transformSetToSerializable(value);
  }

  if (value instanceof Map) {
    return transformMapToSerializable(value);
  }

  return value;
}

export function replacer<T>(_key: string, value: T): SerializableValue<T> {
  return transformValueToSerializable(value);
}

export function reviver(_key: string, value: unknown) {
  if (
    typeof value === 'object' &&
    value != null &&
    'value' in value &&
    '__type' in value
  ) {
    if (value.__type === 'Date' && typeof value.value === 'string') {
      return new Date(value.value);
    }

    if (value.__type === 'Set' && Array.isArray(value.value)) {
      return new Set(value.value);
    }

    if (
      value.__type === 'Map' &&
      value.value != null &&
      typeof value.value === 'object'
    ) {
      return new Map(Object.entries(value.value));
    }
  }

  return value;
}

export function serialize<T>(value: T): string {
  return JSON.stringify(transformValueToSerializable(value), replacer);
}

export function deserialize<T>(value: string): T {
  return JSON.parse(value, reviver) as T;
}
